import { Colors } from "@/enums/Colors";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, Checkbox, Collapse, Typography, styled } from "@mui/material";
import { useState } from "react";
import { Pill } from "../task-components/Pill";
import { DataNode, HeaderCells } from "./CommonTable";

const MobileCard = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  borderRadius: "0.75rem",
  border: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  backgroundColor: Colors.WHITE,
  marginBottom: "0.75rem",
  transition: "all 0.2s ease",
  position: "relative",
  overflow: "hidden",
});

interface CardHeaderProps {
  open: boolean;
}

const CardHeader = styled(Box)(({ open }: CardHeaderProps) => ({
  display: "flex",
  alignItems: "center",
  width: "100%",
  borderRadius: open ? "0.75rem 0.75rem 0 0" : "0.6875rem",
  padding: "1rem 0.5rem",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  borderBottom: open ? `1px solid ${Colors.LIGHT_GRAY_DEDEE1}` : "none",
  userSelect: "none",
}));

const PrimaryContent = styled(Box)({
  flex: 1,
  display: "flex",
  flexDirection: "column",
  gap: "0.250rem",
  minWidth: 0,
});

const SecondaryContent = styled(Box)({
  display: "flex",
  flexDirection: "column",
  width: "100%",
  padding: "1rem",
  backgroundColor: Colors.WHITE,
  gap: "0.75rem",
});

const DataRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "0.625rem 0",
  borderBottom: `1px solid ${Colors.LIGHT_GRAY_F3F3F3}`,
  "&:last-child": {
    borderBottom: "none",
  },
  minHeight: "2rem",
  gap: "1rem",
});

const DataLabel = styled(Typography)({
  fontSize: "0.75rem",
  fontWeight: 600,
  color: Colors.LIGHT_GRAY_8A8E93,
  flexShrink: 0,
  width: "40%",
  lineHeight: "1.25rem",
  textTransform: "uppercase",
  letterSpacing: "0.02em",
});

const DataValue = styled(Box)({
  fontSize: "0.875rem",
  fontWeight: 400,
  textAlign: "right",
  flex: 1,
  color: Colors.ELEGANT_BLACK,
  overflow: "hidden",
  textOverflow: "ellipsis",
  lineHeight: "1.25rem",
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  "& > *": {
    maxWidth: "100%",
  },
});

const ActionsContainer = styled(Box)({
  display: "flex",
  justifyContent: "flex-end",
  alignItems: "center",
  gap: "0.5rem",
  padding: "0.75rem 1rem",
  backgroundColor: Colors.LIGHT_GRAY_BG_F6F8FA,
  borderTop: `1px solid ${Colors.LIGHT_GRAY_DEDEE1}`,
  borderRadius: "0 0 0.75rem 0.75rem",
  minHeight: "3rem",
});

export type MobileCardConfig = {
  primaryFieldIndex?: number;
  showExpandButton?: boolean;
  expandedByDefault?: boolean;
  hideSecondaryFields?: boolean;
  collapsedFieldsCount?: number;
  priorityFields?: number[];
  alwaysShowActions?: boolean;
};

interface CommonTableMobileCardProps {
  node: DataNode;
  headerCells: HeaderCells[];
  onCheckboxClick?: (node: DataNode, checked: boolean) => void;
  onClickedRow?: (node: DataNode) => void;
  actions?: JSX.Element[];
  isDeleting?: boolean;
  hideCheckboxes?: boolean;
  mobileCardConfig?: MobileCardConfig;
}

export function CommonTableMobileCard({
  node,
  headerCells,
  onCheckboxClick,
  onClickedRow,
  actions,
  isDeleting,
  hideCheckboxes,
  mobileCardConfig = {},
}: CommonTableMobileCardProps) {
  const {
    primaryFieldIndex = 0,
    showExpandButton = true,
    expandedByDefault = false,
    hideSecondaryFields = false,
    collapsedFieldsCount = 2,
    priorityFields,
    alwaysShowActions = true,
  } = mobileCardConfig;

  const [isExpanded, setIsExpanded] = useState(expandedByDefault);

  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const checked = (e.target as any).checked;
    onCheckboxClick?.(node, checked);
  };

  const handleHeaderClick = (e: React.MouseEvent) => {
    // Prevent header click if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest('input[type="checkbox"]') || target.closest("button") || target.closest('[role="button"]')) {
      return;
    }

    if (showExpandButton && hasHiddenFields) {
      setIsExpanded(!isExpanded);
    }

    if (onClickedRow && !isExpanded) {
      onClickedRow(node);
    }
  };

  const handleBodyClick = (e: React.MouseEvent) => {
    // Prevent body click if clicking on interactive elements
    const target = e.target as HTMLElement;
    if (target.closest("button") || target.closest('[role="button"]') || target.closest(".actions-container")) {
      return;
    }

    if (onClickedRow && isExpanded) {
      onClickedRow(node);
    }
  };

  const getVisibleFields = () => {
    if (priorityFields && priorityFields.length > 0) {
      // Use specified priority fields
      return priorityFields.filter((idx) => idx < node.cells.length && idx !== primaryFieldIndex).slice(0, collapsedFieldsCount);
    } else {
      // Use first N fields after primary field
      const fields = [];
      for (let i = 0; i < node.cells.length && fields.length < collapsedFieldsCount; i++) {
        if (i !== primaryFieldIndex && !headerCells[i]?.sticky) {
          fields.push(i);
        }
      }
      return fields;
    }
  };

  const visibleFieldIndices = getVisibleFields();
  const hasHiddenFields = node.cells.some((_, idx) => idx !== primaryFieldIndex && !visibleFieldIndices.includes(idx));

  return (
    <MobileCard>
      <CardHeader
        sx={{
          cursor: (showExpandButton && hasHiddenFields) || (!isExpanded && onClickedRow) ? "pointer" : "default",
        }}
        open={isExpanded}
        onClick={handleHeaderClick}
      >
        {!hideCheckboxes && (
          <Checkbox
            disabled={isDeleting}
            checked={node.checked}
            onClick={handleCheckboxClick}
            sx={{
              color: Colors.LIGHT_GRAY_DEDEE1,
              "&.MuiCheckbox-root": {
                padding: "0.25rem",
              },
              flexShrink: 0,
              "& .MuiSvgIcon-root": {
                fontSize: "1.5rem", // Increased icon size for better visibility
              },
              "&.Mui-checked": {
                color: Colors.BUTTON_PURPLE,
              },
              "&.Mui-disabled": {
                color: Colors.ALMOST_WHITE,
              },
            }}
          />
        )}

        <PrimaryContent>
          {/* Primary field (usually the name/title) */}
          <Box
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              color: Colors.ELEGANT_BLACK,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
              lineHeight: "1.25rem",
            }}
          >
            {node.cells[primaryFieldIndex]}
          </Box>

          {/* Show priority fields as pills when collapsed */}
          {!isExpanded && !hideSecondaryFields && (
            <Box
              sx={{
                display: "flex",
                gap: "0.5rem",
                flexWrap: "wrap",
                marginTop: "0.375rem",
                alignItems: "center",
              }}
            >
              {visibleFieldIndices.map((idx) => {
                const header = headerCells[idx];
                const cell = node.cells[idx];

                // Skip if header is undefined
                if (!header) return null;

                // Check if the cell content is a React element or simple text
                const isSimpleText = typeof cell === "string" || typeof cell === "number";

                if (isSimpleText) {
                  return (
                    <Pill key={idx} borderColor={Colors.LIGHT_GRAY_DEDEE1} backgroundColor={Colors.LIGHT_GRAY_BG_F6F8FA}>
                      <Typography className="pill-text" sx={{ fontSize: "0.75rem", color: Colors.LIGHT_GRAY_8A8E93 }}>
                        {header.label}:
                      </Typography>
                      <Typography className="pill-text" sx={{ fontSize: "0.75rem", color: Colors.ELEGANT_BLACK, fontWeight: 500 }}>
                        {cell}
                      </Typography>
                    </Pill>
                  );
                } else {
                  // For complex elements, render them directly with smaller styling
                  return (
                    <Box
                      key={idx}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.25rem",
                        "& > *": {
                          fontSize: "0.75rem !important",
                          height: "auto",
                          minHeight: "unset",
                        },
                      }}
                    >
                      {cell}
                    </Box>
                  );
                }
              })}
            </Box>
          )}
        </PrimaryContent>

        {showExpandButton && hasHiddenFields && (
          <Box
            sx={{
              padding: "0.375rem",
              color: isExpanded ? Colors.BUTTON_PURPLE : Colors.LIGHT_GRAY_8A8E93,
              flexShrink: 0,
              transition: "all 0.2s ease",
              display: "flex",
              alignItems: "center",
            }}
          >
            {isExpanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
          </Box>
        )}
      </CardHeader>

      {/* Expanded content */}
      <Collapse in={isExpanded}>
        <SecondaryContent
          onClick={handleBodyClick}
          sx={{
            cursor: onClickedRow ? "pointer" : "default",
          }}
        >
          {node.cells.map((cell, idx) => {
            if (idx === primaryFieldIndex) return null;
            const header = headerCells[idx];

            // Skip if header is undefined
            if (!header) return null;

            return (
              <DataRow key={idx}>
                <DataLabel>{header.label}</DataLabel>
                <DataValue>{cell}</DataValue>
              </DataRow>
            );
          })}
        </SecondaryContent>
      </Collapse>

      {/* Actions */}
      {actions && actions.length > 0 && (alwaysShowActions || isExpanded) && (
        <ActionsContainer className="actions-container" onClick={(e: React.MouseEvent) => e.stopPropagation()}>
          {actions.map((action, idx) => (
            <Box
              key={idx}
              sx={{
                "& button": {
                  fontSize: "0.75rem",
                  padding: "0.375rem 0.75rem",
                  minHeight: "2rem",
                },
                "& .MuiIconButton-root": {
                  padding: "0.375rem",
                },
              }}
            >
              {action}
            </Box>
          ))}
        </ActionsContainer>
      )}
    </MobileCard>
  );
}
